import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { BatchCloneRow } from './components/batch-cloning-table/batch-cloning-table.component';
import { BatchCloningService } from '@main/batch-cloning.service';
import { LoaderService } from '@app-shared/services/loader.service';
import { AlertService } from '@main/alert.service';
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { LogsTableComponent } from './components/logs-table/logs-table.component';
import { CalendarCommonModule } from "angular-calendar";
import { AutomationJobRow, AutomationJobStatus, BulkBatchCloningTableComponent } from './components/bulk-batch-cloning-table/bulk-batch-cloning-table.component';
import { ScheduleRow, ScheduleTableComponent } from './components/schedule-table/schedule-table.component';
import { ScheduleRescheduleModalResult } from './components/schedule-reschedule-modal/schedule-reschedule-modal.component';
import { ScheduleCalendarDateChange, ScheduleCalendarJobsComponent } from './components/schedule-calendar-jobs/schedule-calendar-jobs.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-automation',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet, CalendarCommonModule, BulkBatchCloningTableComponent, ScheduleTableComponent, ScheduleCalendarJobsComponent],
  templateUrl: './automation.component.html',
  styleUrls: ['./automation.component.css'],
})

export class AutomationComponent implements OnInit {
  location = inject(Location);
  faAngleLeft = faAngleLeft;
  rows = signal<BatchCloneRow[]>([]);
  logs = signal<LogsTableComponent[]>([]);
  isLoading = signal(false);
  jobsFromDate = signal('');
  jobsToDate = signal('');
  jobRows = signal<AutomationJobRow[]>([]);
  jobCount = signal(0);
  jobsLoading = signal(false);
  scheduleData = signal<ScheduleRow[]>([]);
  scheduleTotal = signal(0);

  schedulePage = signal(1);
  scheduleLimit = signal(10);
  scheduleSearch = signal('');

  scheduleRows = signal<ScheduleRow[]>([]);
  scheduleCount = signal(0);
  scheduleLoading = signal(false);

  scheduleCalendarMonth = signal(new Date().getMonth() + 1);
  scheduleCalendarYear = signal(new Date().getFullYear());

  scheduleCalendarRows = signal<ScheduleRow[]>([]);
  scheduleCalendarLoading = signal(false);

  constructor(
    private loaderService: LoaderService,
    private batchCloningService: BatchCloningService,
    private alertService: AlertService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.getCloningJobsList();
    this.getCloningScheduleTable();
    this.getCloningScheduleTableByDate();
  }

  private mapJobRow(item: any): AutomationJobRow {
    const normalizedStatus: AutomationJobStatus =
      item?.status === 'completed'
        ? 'completed'
        : item?.status === 'failed'
          ? 'failed'
          : 'in_progress';

    return {
      id: item?.id,
      jobId: item?.jobId ?? '',
      createdAt: item?.createdAt,
      completedAt: item?.completedAt,
      createdBy: item?.createdByName ?? item?.createdBy ?? '',
      createdByName: item?.createdByName ?? '',
      status: normalizedStatus,
      inProgress: !!item?.inProgress,
      totalItems: item?.totalItems ?? 0,
      successCount: item?.successCount ?? 0,
      failedCount: item?.failedCount ?? 0,
      singleProjects: item?.typeCounts?.single ?? 0,
      myaCount: item?.typeCounts?.mya ?? 0,
      expiringCount: item?.typeCounts?.expiring ?? 0,
    };
  }

  getCloningJobsList(): void {
    this.jobsLoading.set(true);
    this.loaderService.showSpinner();

    this.batchCloningService
      .getCloningJobsList(this.jobsFromDate(), this.jobsToDate())
      .subscribe({
        next: (res) => {
          const items = res?.items ?? [];
          this.jobRows.set(items.map((item: any) => this.mapJobRow(item)));
          this.jobCount.set(res?.count ?? items.length);
          this.jobsLoading.set(false);
          this.loaderService.hideSpinner();
        },
        error: (error) => {
          this.jobsLoading.set(false);
          this.loaderService.hideSpinner();
          this.alertService.showErrorAlert(
            error?.error?.message || 'Failed to load cloning jobs list.'
          );
        },
      });
  }

  applyJobsFilters(): void {
    this.getCloningJobsList();
  }

  clearJobsFilters(): void {
    this.jobsFromDate.set('');
    this.jobsToDate.set('');
    this.getCloningJobsList();
  }

  viewJobDetails(row: any): void {
    let id = row.jobId || row.source;
    this.router.navigateByUrl(`clone-history-details/${id}`)
  }


  getCloningScheduleTable(): void {
    this.scheduleLoading.set(true);
    this.loaderService.showSpinner();

    const limit = this.scheduleLimit();
    const offset = (this.schedulePage() - 1) * limit;

    this.batchCloningService
      .getCloningSchedule({
        offset,
        limit,
        searchTerm: this.scheduleSearch().trim(),
      })
      .subscribe({
        next: (res) => {
          const items = res?.items ?? [];

          this.scheduleRows.set(items);
          this.scheduleCount.set(res?.count ?? items.length);

          this.scheduleLoading.set(false);
          this.loaderService.hideSpinner();
        },
        error: (error) => {
          this.scheduleLoading.set(false);
          this.loaderService.hideSpinner();

          this.alertService.showErrorAlert(
            error?.error?.message ||
            'Failed to load cloning schedule.'
          );
        },
      });
  }

  getCloningScheduleTableByDate(): void {
    this.scheduleLoading.set(true);
    this.loaderService.showSpinner();

    this.batchCloningService
      .getCloningScheduleByDate({
        month: this.scheduleCalendarMonth(),
        year: this.scheduleCalendarYear(),
      })
      .subscribe({
        next: (res) => {
          this.scheduleCalendarRows.set(res?.scheduleItems ?? []);
          this.scheduleLoading.set(false);
          this.loaderService.hideSpinner();
        },
        error: (error) => {
          this.scheduleLoading.set(false);
          this.loaderService.hideSpinner();

          this.alertService.showErrorAlert(
            error?.error?.message ||
            'Failed to load cloning schedule.'
          );
        },
      });
  }

  onScheduleCalendarDateChange(
    date: ScheduleCalendarDateChange,
  ): void {
    this.scheduleCalendarMonth.set(date.month);
    this.scheduleCalendarYear.set(date.year);

    this.getCloningScheduleTableByDate();
  }
  
  onSchedulePageChange(page: number): void {
    this.schedulePage.set(page);
    this.getCloningScheduleTable();
  }

  onScheduleLimitChange(limit: number): void {
    this.scheduleLimit.set(limit);
    this.schedulePage.set(1);

    this.getCloningScheduleTable();
  }

  onScheduleSearch(search: string): void {
    this.scheduleSearch.set(search);
    this.schedulePage.set(1);

    this.getCloningScheduleTable();
  }

  onRetrySchedule(row: ScheduleRow) {
    this.batchCloningService.retryCloningSchedule(row.scheduleId).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Successfully retried schdule!');
        this.getCloningScheduleTable()
      },
    });
  }

  onCancelSchedule(row: ScheduleRow) {
    this.batchCloningService.cancelCloningSchedule(row.scheduleId).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Successfully cancelled schdule!');
        this.getCloningScheduleTable()
      },
    });
  }

  onRescheduleSchedule(event: ScheduleRescheduleModalResult) {
    this.batchCloningService
      .rescheduleCloningSchedule(event.row.scheduleId, {
        sendAt: event.sendAt,
      })
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Successfully rescheduled!');
          this.getCloningScheduleTable()
        },
      });
  }
}
